import rotateUrl from "../assets/rotate.svg?url";
import { drawCharacter } from "../entities/Player";
import { DIFFICULTY, HAIR_COLORS, OUTFIT_COLORS, SKIN_TONES } from "../game/constants";
import { boardTitleKey, difficultyFromSlider, gameOverCheerKind, hudButtonAction, sliderFromDifficulty } from "../game/flow";
import { accuracyPercent, isPiercingActive, powerRemainingMs } from "../game/rules";
import { goldBalloonDataUrl } from "../entities/Balloon";
import { PWA_ENABLED } from "../config/variant";
import { renderInstallCard, renderPrivateBetaNotice } from "../pwa/installUi";
import type { InstallUiState } from "../pwa/policy";
import { beachLogoMarkup, layoutBeachLogo } from "./logoMarkup";
import type { LocalizationSystem } from "../systems/LocalizationSystem";
import type { PlayController, TutorialStep } from "../systems/PlayController";
import type { SaveSystem } from "../systems/SaveSystem";
import type { CharacterConfig, DifficultyId, Language, MatchState, ScoreEntry } from "../game/types";

export type UiScreen =
  | "menu"
  | "character"
  | "instructions"
  | "pause"
  | "help"
  | "gameover"
  | "scores"
  | "options"
  | "confirm"
  | "countdown"
  | "none";

export type SettingsTab = "sound" | "access" | "general" | "help" | "scores" | "data";

export interface UiHandlers {
  play(): void;
  playTutorial(): void;
  open(screen: UiScreen): void;
  resume(): void;
  restart(): void;
  toMenu(): void;
  skipTutorial(): void;
  tutorialNext(): void;
  setLanguage(language: Language): void;
  setDifficulty(id: DifficultyId): void;
  setNickname(name: string): void;
  saveCharacter(config: CharacterConfig): void;
  toggleFullscreen(): void;
  install(): void;
  setVolume(kind: "music" | "ambient" | "sfx", value: number): void;
  setAccessibility(): void;
  clearScores(): void;
  resetAll(): void;
}

export class UiRoot {
  private screen: UiScreen = "menu";
  private confirmKind: "clear" | "reset" | null = null;
  private message = "";
  private draft: CharacterConfig;
  private settingsTab: SettingsTab = "sound";
  private hudReady = false;
  private hudTut: string | null = "\0";
  private hudMaxLives = -1;
  private prevLives = -1;
  private pickDifficulty = false;
  private gameOverMatch: MatchState | null = null;
  private gameOverBoard: ScoreEntry[] = [];
  private gameOverRecord = false;
  private hudLock = false;
  private logoTimer: number | null = null;
  installUi: InstallUiState = "off";
  fullscreen = false;

  constructor(
    private readonly root: HTMLElement,
    private readonly hud: HTMLElement,
    private readonly rotate: HTMLElement,
    private readonly i18n: LocalizationSystem,
    private readonly save: SaveSystem,
    private readonly handlers: UiHandlers,
  ) {
    this.draft = { ...save.data.character };
    this.root.addEventListener("click", this.onClick);
    this.root.addEventListener("input", this.onInput);
    this.root.addEventListener("change", this.onChange);
    this.hud.addEventListener("pointerdown", this.onHudPointer, { capture: true });
    this.hud.addEventListener("click", this.onHudPointer, { capture: true });
  }

  show(screen: UiScreen): void {
    if (screen === "scores") {
      this.screen = "options";
      this.settingsTab = "scores";
      this.render();
      return;
    }
    this.screen = screen;
    if (screen === "options") this.settingsTab = this.settingsTab || "sound";
    this.render();
  }

  current(): UiScreen {
    return this.screen;
  }

  applyChrome(): void {
    const acc = this.save.data.accessibility;
    document.documentElement.style.setProperty("--text-scale", String(acc.textScale));
    document.documentElement.classList.toggle("high-contrast", acc.highContrast);
    document.documentElement.classList.toggle("reduce-motion", acc.reduceMotion);
    document.documentElement.classList.toggle("color-assist", acc.colorAssist);
  }

  render(): void {
    this.applyChrome();
    const t = this.i18n.t;
    if (this.screen === "none" || this.screen === "countdown") {
      this.stopLogoPop();
      this.root.innerHTML =
        this.screen === "countdown"
          ? `<div class="overlay"><div class="panel countdown" data-act="noop"><p>${t.countdown}</p><strong id="count-num">3</strong></div></div>`
          : "";
      return;
    }
    if (this.screen === "gameover" && this.gameOverMatch) {
      this.stopLogoPop();
      this.root.innerHTML = this.gameOverMarkup(this.gameOverMatch, this.gameOverBoard, this.gameOverRecord);
      return;
    }
    this.root.innerHTML = this.markup();
    this.refreshLogoPop();
  }

  setCountdown(n: number): void {
    const el = this.root.querySelector("#count-num");
    if (el) el.textContent = String(n);
  }

  invalidateHud(): void {
    this.hudReady = false;
    this.hudTut = "\0";
  }

  renderHud(play: PlayController, tutorialText: string | null): void {
    this.hud.hidden = false;
    const maxLives = play.match.maxLives;
    if (!this.hudReady || this.hudTut !== tutorialText || this.hudMaxLives !== maxLives) {
      this.buildHud(play, tutorialText);
    }
    this.patchHud(play);
  }

  hideHud(): void {
    this.hud.hidden = true;
    this.hud.innerHTML = "";
    this.hudReady = false;
    this.hudTut = "\0";
  }

  renderRotate(portrait: boolean): void {
    const t = this.i18n.t;
    this.rotate.hidden = !portrait;
    if (!portrait) {
      this.rotate.innerHTML = "";
      return;
    }
    this.rotate.innerHTML = `
      <div class="rotate-inner">
        <img src="${rotateUrl}" alt="" width="280" height="158" />
        <h2>${t.rotate.title}</h2>
        <p>${t.rotate.message}</p>
      </div>
    `;
  }

  gameOverMarkup(match: MatchState, board: ScoreEntry[], record: boolean): string {
    const t = this.i18n.t;
    const reason = match.gameOverReason === "frisbees" ? t.gameover.noFrisbees : t.gameover.noLives;
    const cheerKey = gameOverCheerKind(match.score, record);
    const cheer =
      cheerKey === "record"
        ? t.gameover.cheerRecord
        : cheerKey === "great"
          ? t.gameover.cheerGreat
          : cheerKey === "good"
            ? t.gameover.cheerGood
            : t.gameover.cheerOk;
    const nick = this.save.data.character.nickname;
    const diff = this.pickDifficulty
      ? `<div class="diff-pick"><p>${t.gameover.chooseDifficulty}</p>${this.diffButtons(this.save.data.difficulty, "replay-diff")}</div>`
      : "";
    return `
      <div class="overlay results-overlay">
        <div class="results-layout">
          <div class="results-main">
            <h1 class="cheer">${cheer}</h1>
            <p class="reason">${reason}</p>
            <p class="final-score">${t.gameover.score} <strong>${match.score}</strong></p>
            ${record ? `<p class="record-banner">${t.gameover.newRecord}</p>` : ""}
            <div class="stat-cards">
              ${statCard("combo", t.gameover.bestCombo, `x${match.bestCombo}`)}
              ${statCard("balloon", t.gameover.popped, String(match.balloonsPopped))}
              ${statCard("frisbee", t.gameover.shots, String(match.shots))}
              ${statCard("hit", t.gameover.hits, String(match.hits))}
              ${statCard("target", t.gameover.accuracy, `${accuracyPercent(match)}%`)}
            </div>
            ${diff}
            <div class="row wrap results-actions">
              <button class="btn-play" data-act="replay">${t.gameover.replay}</button>
              <button class="ghost" data-act="change-diff">${t.gameover.changeDifficulty}</button>
              <button class="ghost" data-act="menu">${t.gameover.menu}</button>
            </div>
          </div>
          <aside class="results-board">
            <h3>${t.gameover[boardTitleKey(match.difficulty)]}</h3>
            ${this.scoreCards(board, nick, match.score)}
          </aside>
        </div>
      </div>`;
  }

  showGameOver(match: MatchState, board: ScoreEntry[], record: boolean): void {
    this.screen = "gameover";
    this.pickDifficulty = false;
    this.gameOverMatch = match;
    this.gameOverBoard = board;
    this.gameOverRecord = record;
    this.root.innerHTML = this.gameOverMarkup(match, board, record);
  }

  paintCharacterPreview(time: number): void {
    const canvas = this.root.querySelector("#char-preview") as HTMLCanvasElement | null;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.fillStyle = "#f3d27a";
    ctx.beginPath();
    ctx.ellipse(140, 338, 100, 22, 0, 0, Math.PI * 2);
    ctx.fill();
    const reduce = this.save.data.accessibility.reduceMotion;
    const t = reduce ? 0 : time;
    ctx.save();
    ctx.translate(140, 318);
    ctx.scale(1.55, 1.55);
    drawCharacter(ctx, this.draft, 0, 0, "idle", t, false, reduce);
    ctx.restore();
  }

  tutorialKey(step: TutorialStep): string | null {
    if (!step || step === "done") return null;
    return this.i18n.t.tutorial[step];
  }

  private buildHud(play: PlayController, tutorialText: string | null): void {
    const t = this.i18n.t;
    const m = play.match;
    const hearts = Array.from({ length: m.maxLives }, (_, i) => `<span class="heart ${i < m.lives ? "on" : "off"}" data-heart="${i}">❤</span>`).join("");
    const gold = isPiercingActive(m, play.state.simTimeMs);
    const ammo = Array.from({ length: 3 }, (_, i) => `<span class="ammo ${i < m.frisbees ? "on" : "off"} ${gold ? "gold" : "red"}"></span>`).join("");
    const tut = tutorialText
      ? `<div class="tutorial-card"><p>${tutorialText}</p><div class="row"><button type="button" data-act="tut-next">${t.tutorial.next}</button><button type="button" class="ghost" data-act="tut-skip">${t.tutorial.skip}</button></div></div>`
      : "";
    this.hud.innerHTML = `
      <button type="button" class="pause-btn" data-act="pause" aria-label="${t.hud.pause}">⏸</button>
      <div class="hud-top">
        <div>${t.hud.score} <strong id="hud-score">${m.score}</strong></div>
        <div>${t.hud.combo} <strong id="hud-combo">x${Math.max(1, m.combo)}</strong></div>
      </div>
      <div class="hud-hearts">${hearts}</div>
      <div class="hud-ammo">${ammo}</div>
      <div class="power" id="hud-power" hidden></div>
      ${tut}
    `;
    this.hudReady = true;
    this.hudTut = tutorialText;
    this.hudMaxLives = m.maxLives;
    this.prevLives = m.lives;
  }

  private patchHud(play: PlayController): void {
    const t = this.i18n.t;
    const m = play.match;
    const score = this.hud.querySelector("#hud-score");
    const combo = this.hud.querySelector("#hud-combo");
    if (score) score.textContent = String(m.score);
    if (combo) combo.textContent = `x${Math.max(1, m.combo)}`;
    const reduce = this.save.data.accessibility.reduceMotion;
    this.hud.querySelectorAll<HTMLElement>("[data-heart]").forEach((el) => {
      const i = Number(el.dataset.heart);
      const on = i < m.lives;
      el.classList.toggle("on", on);
      el.classList.toggle("off", !on);
      if (!on && this.prevLives > m.lives && i === m.lives) {
        el.classList.toggle("lost", !reduce);
        if (!reduce) window.setTimeout(() => el.classList.remove("lost"), 420);
      }
    });
    this.prevLives = m.lives;
    const gold = isPiercingActive(m, play.state.simTimeMs);
    this.hud.querySelectorAll<HTMLElement>(".ammo").forEach((el, i) => {
      el.classList.toggle("on", i < m.frisbees);
      el.classList.toggle("off", i >= m.frisbees);
      el.classList.toggle("gold", gold);
      el.classList.toggle("red", !gold);
    });
    const power = this.hud.querySelector<HTMLElement>("#hud-power");
    if (power) {
      const remain = powerRemainingMs(m, play.state.simTimeMs);
      if (remain > 0) {
        power.hidden = false;
        power.innerHTML = `${t.hud.power} <strong>${Math.ceil(remain / 1000)}s</strong>`;
      } else {
        power.hidden = true;
      }
    }
  }

  private markup(): string {
    const t = this.i18n.t;
    const d = this.save.data;
    if (this.screen === "menu") {
      return `
      <div class="overlay menu-overlay">
        <div class="menu-screen">
          ${beachLogoMarkup(d.accessibility.reduceMotion)}
          <button type="button" class="btn-play" data-act="play">${t.menu.play}</button>
          ${this.diffSlider(d.difficulty)}
          <div class="menu-mini">
            <button type="button" class="ghost" data-act="character">${t.menu.customize}</button>
            <button type="button" class="ghost" data-act="options">${t.menu.settings}</button>
          </div>
          ${renderPrivateBetaNotice(PWA_ENABLED, t.privateBetaNotice)}
        </div>
      </div>`;
    }
    if (this.screen === "character") {
      const c = this.draft;
      return `
      <div class="overlay customize-overlay">
        <div class="customize-screen">
          <div class="customize-panel panel">
            <h2>${t.character.title}</h2>
            <label>${t.menu.nickname}<input data-act="nick" maxlength="16" value="${escapeAttr(c.nickname)}" placeholder="${t.menu.nicknamePlaceholder}" /></label>
            <div class="row">
              <button type="button" data-act="set-id" data-id="girl" class="${c.id === "girl" ? "" : "ghost"}">${t.character.girl}</button>
              <button type="button" data-act="set-id" data-id="boy" class="${c.id === "boy" ? "" : "ghost"}">${t.character.boy}</button>
            </div>
            <p>${t.character.skin}</p>
            <div class="swatches">${this.swatches("skin", SKIN_TONES, c.skin)}</div>
            <p>${t.character.hair}</p>
            <div class="swatches">${this.swatches("hair", HAIR_COLORS, c.hair)}</div>
            <p>${t.character.outfit}</p>
            <div class="swatches">${this.swatches("outfit", OUTFIT_COLORS, c.outfit)}</div>
            <div class="row">
              <button type="button" data-act="save-char">${t.character.save}</button>
              <button type="button" class="ghost" data-act="menu">${t.character.back}</button>
            </div>
          </div>
          <div class="customize-preview">
            <p>${t.character.preview}</p>
            <canvas id="char-preview" width="280" height="360"></canvas>
          </div>
        </div>
      </div>`;
    }
    if (this.screen === "instructions") {
      return `
      <div class="overlay">
        <div class="panel wide instr-panel">
          <h2>${t.instructions.title}</h2>
          <div class="instr-grid">
            <p>${t.instructions.p1}</p>
            <p>${t.instructions.p2}</p>
            <p>${t.instructions.p3}</p>
            <p>${t.instructions.p4}</p>
          </div>
          <div class="row wrap">
            <button type="button" data-act="play-tutorial">${t.instructions.playTutorial}</button>
            <button type="button" class="ghost" data-act="skip-tutorial">${t.instructions.skipTutorial}</button>
            <button type="button" class="ghost" data-act="menu">${t.instructions.back}</button>
          </div>
        </div>
      </div>`;
    }
    if (this.screen === "pause") {
      const v = d.volumes;
      const a = d.accessibility;
      return `
      <div class="overlay pause-overlay">
        <div class="pause-shell">
          <h2>${t.pause.title}</h2>
          <div class="pause-grid">
            <section class="pause-card pause-actions">
              <button type="button" class="btn-play" data-act="resume">${t.pause.resume}</button>
              <button type="button" class="ghost" data-act="restart">${t.pause.restart}</button>
              <button type="button" class="ghost" data-act="menu">${t.pause.menu}</button>
            </section>
            <section class="pause-card pause-audio">
              ${this.slider("music", t.pause.music, v.music)}
              ${this.slider("ambient", t.pause.ambient, v.ambient)}
              ${this.slider("sfx", t.pause.sfx, v.sfx)}
            </section>
            <section class="pause-card pause-access">
              <h3>${t.pause.accessibility}</h3>
              <div class="pause-checks">
                <label class="check"><input type="checkbox" data-acc="screenShake" ${a.screenShake ? "checked" : ""} /> ${t.options.shake}</label>
                <label class="check"><input type="checkbox" data-acc="reduceMotion" ${a.reduceMotion ? "checked" : ""} /> ${t.options.reduceMotion}</label>
                <label class="check"><input type="checkbox" data-acc="highContrast" ${a.highContrast ? "checked" : ""} /> ${t.options.highContrast}</label>
                <label class="check"><input type="checkbox" data-acc="colorAssist" ${a.colorAssist ? "checked" : ""} /> ${t.options.colorAssist}</label>
              </div>
              <label>${t.options.textSize}<input type="range" min="0.9" max="1.25" step="0.05" data-acc="textScale" value="${a.textScale}" /></label>
              <label>${t.options.aimLength}<input type="range" min="0.7" max="1.15" step="0.05" data-acc="aimLengthScale" value="${a.aimLengthScale}" /></label>
            </section>
            <section class="pause-card pause-extra">
              <button type="button" class="ghost" data-act="help">${t.pause.help}</button>
              <button type="button" class="ghost" data-act="fullscreen">${this.fullscreen ? t.menu.exitFullscreen : t.pause.fullscreen}</button>
            </section>
          </div>
        </div>
      </div>`;
    }
    if (this.screen === "help") {
      return `
      <div class="overlay">
        <div class="panel">
          <h2>${t.help.title}</h2>
          <p>${t.help.body}</p>
          <button type="button" data-act="pause">${t.help.close}</button>
        </div>
      </div>`;
    }
    if (this.screen === "options") {
      return this.settingsMarkup();
    }
    if (this.screen === "confirm") {
      const msg = this.confirmKind === "reset" ? t.options.confirmReset : t.options.confirmClear;
      return `
      <div class="overlay">
        <div class="panel">
          <h2>${t.options.title}</h2>
          <p>${msg}</p>
          <div class="row">
            <button type="button" class="danger" data-act="confirm-yes">${t.options.yes}</button>
            <button type="button" class="ghost" data-act="options">${t.options.no}</button>
          </div>
        </div>
      </div>`;
    }
    return "";
  }

  private settingsMarkup(): string {
    const t = this.i18n.t;
    const d = this.save.data;
    const a = d.accessibility;
    const v = d.volumes;
    const tab = this.settingsTab;
    const tabs: { id: SettingsTab; label: string }[] = [
      { id: "sound", label: t.options.tabSound },
      { id: "access", label: t.options.tabAccess },
      { id: "general", label: t.options.tabGeneral },
      { id: "help", label: t.options.tabHelp },
      { id: "scores", label: t.options.tabScores },
      { id: "data", label: t.options.tabData },
    ];
    let body = "";
    if (tab === "sound") {
      body = `${this.slider("music", t.pause.music, v.music)}${this.slider("ambient", t.pause.ambient, v.ambient)}${this.slider("sfx", t.pause.sfx, v.sfx)}`;
    } else if (tab === "access") {
      body = `
        <label class="check"><input type="checkbox" data-acc="screenShake" ${a.screenShake ? "checked" : ""} /> ${t.options.shake}</label>
        <label class="check"><input type="checkbox" data-acc="reduceMotion" ${a.reduceMotion ? "checked" : ""} /> ${t.options.reduceMotion}</label>
        <label class="check"><input type="checkbox" data-acc="highContrast" ${a.highContrast ? "checked" : ""} /> ${t.options.highContrast}</label>
        <label class="check"><input type="checkbox" data-acc="colorAssist" ${a.colorAssist ? "checked" : ""} /> ${t.options.colorAssist}</label>
        <label>${t.options.textSize}<input type="range" min="0.9" max="1.25" step="0.05" data-acc="textScale" value="${a.textScale}" /></label>
        <label>${t.options.aimLength}<input type="range" min="0.7" max="1.15" step="0.05" data-acc="aimLengthScale" value="${a.aimLengthScale}" /></label>`;
    } else if (tab === "general") {
      body = `
        <div class="general-grid">
          <button type="button" class="general-card" data-act="lang">
            <span class="g-ico" aria-hidden="true">Aa</span>
            <strong>${d.language === "fr" ? t.languageName + " / English" : "English / Français"}</strong>
            <span class="g-hint">${t.options.langHint}</span>
          </button>
          <button type="button" class="general-card" data-act="fullscreen">
            <span class="g-ico" aria-hidden="true">⛶</span>
            <strong>${this.fullscreen ? t.menu.exitFullscreen : t.menu.fullscreen}</strong>
            <span class="g-hint">${t.options.fullscreenHint}</span>
          </button>
          ${PWA_ENABLED ? renderInstallCard(this.installUi, t) : ""}
          <button type="button" class="general-card" data-act="play-tutorial">
            <span class="g-ico" aria-hidden="true">▶</span>
            <strong>${t.options.replayTutorial}</strong>
            <span class="g-hint">${t.options.tutorialHint}</span>
          </button>
          <button type="button" class="general-card" data-act="instructions">
            <span class="g-ico" aria-hidden="true">?</span>
            <strong>${t.menu.instructions}</strong>
            <span class="g-hint">${t.options.instructionsHint}</span>
          </button>
        </div>
        ${renderPrivateBetaNotice(PWA_ENABLED, t.privateBetaNotice)}`;
    } else if (tab === "help") {
      body = `<p>${t.help.body}</p><p>${t.instructions.p1}</p>`;
    } else if (tab === "scores") {
      body = `${this.diffButtons(d.difficulty)}<div class="board-wrap"><h3>${t.gameover[boardTitleKey(d.difficulty)]}</h3>${this.scoreCards(d.scores[d.difficulty], "", -1)}</div>`;
    } else {
      body = `
        <p class="hint">${this.message}</p>
        <div class="row wrap">
          <button type="button" class="danger ghost" data-act="clear-scores">${t.options.clearScores}</button>
          <button type="button" class="danger ghost" data-act="reset-all">${t.options.resetAll}</button>
        </div>`;
    }
    return `
      <div class="overlay settings-overlay">
        <div class="settings-shell">
          <div class="settings-head">
            <h2>${t.options.title}</h2>
            <button type="button" class="ghost" data-act="menu">${t.options.back}</button>
          </div>
          <nav class="tabs" role="tablist">
            ${tabs
              .map(
                (item) =>
                  `<button type="button" role="tab" class="${item.id === tab ? "on" : "ghost"}" data-act="tab" data-tab="${item.id}">${item.label}</button>`,
              )
              .join("")}
          </nav>
          <div class="settings-body">${body}</div>
        </div>
      </div>`;
  }

  private diffSlider(current: DifficultyId): string {
    const t = this.i18n.t;
    const value = sliderFromDifficulty(current);
    const cfg = DIFFICULTY[current];
    return `
      <div class="diff-slider">
        <p class="diff-title">${t.menu.difficulty}</p>
        <input type="range" min="0" max="2" step="1" value="${value}" data-act="diff-range" aria-label="${t.menu.difficulty}" />
        <div class="diff-labels">
          <span class="${current === "easy" ? "on" : ""}">${t.menu.easy}</span>
          <span class="${current === "normal" ? "on" : ""}">${t.menu.normal}</span>
          <span class="${current === "hard" ? "on" : ""}">${t.menu.hard}</span>
        </div>
        <p class="diff-stats">${t.menu[current === "easy" ? "easy" : current === "hard" ? "hard" : "normal"]} · ❤ ${cfg.lives} ${t.menu.lives} · ⬤ ${cfg.maxBalloons} ${t.menu.balloons}</p>
      </div>`;
  }

  private diffButtons(current: DifficultyId, act = "diff"): string {
    const t = this.i18n.t.menu;
    const ids: DifficultyId[] = ["easy", "normal", "hard"];
    const labels = { easy: t.easy, normal: t.normal, hard: t.hard };
    return `<div class="row wrap">${ids
      .map((id) => `<button type="button" data-act="${act}" data-id="${id}" class="${id === current ? "" : "ghost"}">${labels[id]}</button>`)
      .join("")}</div>`;
  }

  private scoreCards(list: ScoreEntry[], nick: string, score: number): string {
    const t = this.i18n.t;
    if (list.length === 0) return `<p>${t.scores.empty}</p>`;
    return `<ol class="score-cards">${list
      .map((e, i) => {
        const mine = nick !== "" && e.nickname === nick && e.score === score && i === list.findIndex((x) => x.nickname === nick && x.score === score);
        return `<li class="${mine ? "mine" : ""}">
          <span class="rank">${i + 1}</span>
          <strong>${escapeHtml(e.nickname)}</strong>
          <span class="pts">${e.score}</span>
          <span class="meta">x${e.bestCombo} · ${e.accuracy}%</span>
        </li>`;
      })
      .join("")}</ol>`;
  }

  private swatches(key: "skin" | "hair" | "outfit", colors: string[], current: string): string {
    return colors
      .map(
        (c) =>
          `<button type="button" class="swatch ${c === current ? "on" : ""}" data-act="swatch" data-key="${key}" data-color="${c}" style="background:${c}" aria-label="${key}"></button>`,
      )
      .join("");
  }

  private slider(kind: "music" | "ambient" | "sfx", label: string, value: number): string {
    return `<label class="vol-row"><span>${label}</span><input type="range" min="0" max="1" step="0.01" data-vol="${kind}" value="${value}" /><span class="vol-pct">${Math.round(value * 100)}%</span></label>`;
  }

  private stopLogoPop(): void {
    if (this.logoTimer !== null) {
      window.clearInterval(this.logoTimer);
      this.logoTimer = null;
    }
  }

  private resetLogoBall(logo: Element): void {
    logo.classList.remove("popping");
    const ball = logo.querySelector(".logo-ball");
    if (ball instanceof SVGElement) {
      ball.style.removeProperty("transform");
      ball.style.removeProperty("opacity");
      ball.style.removeProperty("clip-path");
    }
  }

  private refreshLogoPop(): void {
    this.stopLogoPop();
    this.paintLogoGold();
    const logo = this.root.querySelector(".beach-logo");
    if (!logo) return;
    this.resetLogoBall(logo);
    if (this.screen !== "menu" || this.save.data.accessibility.reduceMotion) return;
    const ball = logo.querySelector(".logo-ball");
    const pop = (): void => {
      this.resetLogoBall(logo);
      void (logo as HTMLElement).getBoundingClientRect();
      logo.classList.add("popping");
    };
    ball?.addEventListener("animationend", (event: Event) => {
      if ((event as AnimationEvent).animationName !== "logo-o-pop") return;
      this.resetLogoBall(logo);
    });
    window.setTimeout(pop, 2200);
    this.logoTimer = window.setInterval(pop, 15000);
  }

  private paintLogoGold(): void {
    const img = this.root.querySelector(".logo-gold-img");
    if (!(img instanceof SVGImageElement)) return;
    const acc = this.save.data.accessibility;
    img.setAttribute(
      "href",
      goldBalloonDataUrl(256, {
        colorAssist: acc.colorAssist,
        highContrast: acc.highContrast,
        reduceMotion: acc.reduceMotion,
        shadow: true,
      }),
    );
    const svg = this.root.querySelector(".beach-logo");
    if (svg instanceof SVGSVGElement) layoutBeachLogo(svg);
  }

  private onHudPointer = (event: Event): void => {
    const target = (event.target as HTMLElement).closest("[data-act]") as HTMLElement | null;
    if (!target) return;
    const act = hudButtonAction(target.dataset.act);
    if (!act) return;
    event.preventDefault();
    event.stopPropagation();
    if (this.hudLock) return;
    this.hudLock = true;
    window.setTimeout(() => {
      this.hudLock = false;
    }, 180);
    if (act === "pause") this.handlers.open("pause");
    if (act === "tut-skip") this.handlers.skipTutorial();
    if (act === "tut-next") this.handlers.tutorialNext();
  };

  private onClick = (event: Event): void => {
    const target = (event.target as HTMLElement).closest("[data-act]") as HTMLElement | null;
    if (!target) return;
    const act = target.dataset.act;
    switch (act) {
      case "play":
        this.handlers.play();
        break;
      case "character":
        this.draft = { ...this.save.data.character };
        this.handlers.open("character");
        break;
      case "instructions":
        this.handlers.open("instructions");
        break;
      case "options":
        this.message = "";
        this.settingsTab = "sound";
        this.handlers.open("options");
        break;
      case "menu":
        this.handlers.toMenu();
        break;
      case "lang":
        this.handlers.setLanguage(this.save.data.language === "fr" ? "en" : "fr");
        break;
      case "fullscreen":
        this.handlers.toggleFullscreen();
        break;
      case "install":
        this.handlers.install();
        break;
      case "diff":
        this.handlers.setDifficulty((target.dataset.id as DifficultyId) ?? "normal");
        break;
      case "replay-diff":
        this.handlers.setDifficulty((target.dataset.id as DifficultyId) ?? "normal");
        this.handlers.restart();
        break;
      case "set-id":
        this.draft.id = target.dataset.id === "boy" ? "boy" : "girl";
        this.render();
        break;
      case "swatch": {
        const key = target.dataset.key as "skin" | "hair" | "outfit";
        const color = target.dataset.color ?? this.draft[key];
        this.draft[key] = color;
        this.render();
        break;
      }
      case "save-char":
        this.draft.nickname = (this.root.querySelector("[data-act='nick']") as HTMLInputElement | null)?.value ?? this.draft.nickname;
        this.handlers.saveCharacter(this.draft);
        break;
      case "play-tutorial":
        this.handlers.playTutorial();
        break;
      case "skip-tutorial":
        this.handlers.skipTutorial();
        break;
      case "resume":
        this.handlers.resume();
        break;
      case "restart":
        this.handlers.restart();
        break;
      case "help":
        this.handlers.open("help");
        break;
      case "pause":
        this.handlers.open("pause");
        break;
      case "replay":
        this.handlers.restart();
        break;
      case "change-diff":
        this.pickDifficulty = true;
        if (this.gameOverMatch) this.root.innerHTML = this.gameOverMarkup(this.gameOverMatch, this.gameOverBoard, this.gameOverRecord);
        break;
      case "tab":
        this.settingsTab = (target.dataset.tab as SettingsTab) ?? "sound";
        this.render();
        break;
      case "clear-scores":
        this.confirmKind = "clear";
        this.show("confirm");
        break;
      case "reset-all":
        this.confirmKind = "reset";
        this.show("confirm");
        break;
      case "confirm-yes":
        if (this.confirmKind === "reset") this.handlers.resetAll();
        else this.handlers.clearScores();
        this.message = this.confirmKind === "reset" ? this.i18n.t.options.resetDone : this.i18n.t.options.cleared;
        this.confirmKind = null;
        this.settingsTab = "data";
        this.show("options");
        break;
      default:
        break;
    }
  };

  private onInput = (event: Event): void => {
    const el = event.target as HTMLInputElement;
    if (el.dataset.act === "nick") {
      this.draft.nickname = el.value;
      if (this.screen !== "character") this.handlers.setNickname(el.value);
    }
    if (el.dataset.act === "diff-range") {
      this.handlers.setDifficulty(difficultyFromSlider(Number(el.value)));
    }
    if (el.dataset.vol) {
      this.handlers.setVolume(el.dataset.vol as "music" | "ambient" | "sfx", Number(el.value));
      const pct = el.parentElement?.querySelector(".vol-pct");
      if (pct) pct.textContent = `${Math.round(Number(el.value) * 100)}%`;
    }
    if (el.dataset.acc === "textScale" || el.dataset.acc === "aimLengthScale") {
      const acc = this.save.data.accessibility;
      if (el.dataset.acc === "textScale") acc.textScale = Number(el.value);
      else acc.aimLengthScale = Number(el.value);
      this.save.setAccessibility(acc);
      this.handlers.setAccessibility();
    }
  };

  private onChange = (event: Event): void => {
    const el = event.target as HTMLInputElement;
    if (!el.dataset.acc) return;
    const acc = this.save.data.accessibility;
    if (el.type === "checkbox") {
      if (el.dataset.acc === "screenShake") acc.screenShake = el.checked;
      if (el.dataset.acc === "reduceMotion") acc.reduceMotion = el.checked;
      if (el.dataset.acc === "highContrast") acc.highContrast = el.checked;
      if (el.dataset.acc === "colorAssist") acc.colorAssist = el.checked;
    }
    this.save.setAccessibility(acc);
    this.handlers.setAccessibility();
    this.applyChrome();
  };
}

function statCard(kind: string, label: string, value: string): string {
  return `<div class="stat-card ${kind}"><span class="icon" aria-hidden="true"></span><p>${label}</p><strong>${value}</strong></div>`;
}

function escapeHtml(value: string): string {
  return value.replaceAll("&", "&amp;").replaceAll("<", "&lt;").replaceAll(">", "&gt;");
}

function escapeAttr(value: string): string {
  return escapeHtml(value).replaceAll('"', "&quot;");
}
